import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart, ShieldCheck, Clock3, Award, MapPin, Phone, Mail } from "lucide-react";
import { publicApi } from "../../lib/publicApi";
import usePublicSettings, { fmtMoney } from "../../lib/usePublicSettings";
import { useT, lbl } from "../../lib/i18n";
import hero1 from "../../assets/hero/hero-1.webp";
import hero2 from "../../assets/hero/hero-2.webp";
import hero3 from "../../assets/hero/hero-3.webp";

import { tr } from "../../lib/i18n";
const DEFAULT_SLIDES = [hero1, hero2, hero3];

export default function HomePage() {
  const { t, lang } = useT();
  const nav = useNavigate();
  const { settings } = usePublicSettings();

  const [featured, setFeatured] = useState([]);
  const [facets, setFacets] = useState({ makes: [], make_counts: [], body_types: [], years: [], total: 0 });
  const [slide, setSlide] = useState(0);
  const [filter, setFilter] = useState({ make: "", body_type: "", year: "", budget: "" });
  const [bodyTab, setBodyTab] = useState("All");
  const [price, setPrice] = useState(15000);
  const [downPct, setDownPct] = useState(20);
  const [term, setTerm] = useState(48);

  const slides = useMemo(() => {
    const configured = (settings?.hero_slides || []).filter(Boolean);
    const singleUrl = settings?.hero_image_url;
    if (settings?.hero_mode === "single" && singleUrl) return [singleUrl];
    if (configured.length) return configured;
    if (singleUrl) return [singleUrl];
    return DEFAULT_SLIDES;
  }, [settings]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: fe }, { data: fac }] = await Promise.all([
          publicApi.get("/vehicles", { params: { limit: 9, sort: "price_desc" } }),
          publicApi.get("/facets").catch(() => ({ data: {} })),
        ]);
        setFeatured(fe.items || []);
        setFacets(fac || {});
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => setSlide((s) => (s + 1) % slides.length), 7000);
    return () => clearInterval(id);
  }, [slides.length]);

  const total = facets.total ?? 0;
  const makeCounts = (facets.make_counts || []).slice(0, 6);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) q.set(k, v); });
    nav(`/cars${q.toString() ? "?" + q.toString() : ""}`);
  };

  const BODY_FILTERS = useMemo(() => ["All", ...(facets.body_types || []).slice(0, 4)], [facets]);
  const featuredFiltered = useMemo(() => {
    if (bodyTab === "All") return featured.slice(0, 6);
    return featured.filter((v) => (v.body_type || "").toLowerCase() === bodyTab.toLowerCase()).slice(0, 6);
  }, [featured, bodyTab]);

  const financeOn = settings?.finance_enabled !== false;
  const rate = Number(settings?.finance_rate_percent ?? 4.5);
  const terms = settings?.finance_terms_months || [24, 36, 48, 60];
  const disclaimer = (settings?.finance_disclaimer && settings.finance_disclaimer !== "Indicative only, not an offer." ? settings.finance_disclaimer : null) || t("px.disclaimer");
  const years = term / 12;
  const financed = price * (1 - downPct / 100);
  const monthly = Math.max(0, Math.round((financed * (1 + (rate / 100) * years)) / term));

  const promise = (settings?.promise_rows?.length ? settings.promise_rows : [1, 2, 3].map((n) => ({ title: t(`px.p${n /* i18n-keys: 1,2,3 */}_t`), subtitle: t(`px.p${n /* i18n-keys: 1,2,3 */}_s`) })));
  const promiseShown = promise.filter((p) => p && p.title);

  const reviews = (settings?.reviews || []).filter((r) => r && r.quote);
  const rating = settings?.reviews_rating || (reviews.length ? 4.8 : null);
  const reviewCount = settings?.reviews_count || reviews.length;
  const [reviewIdx, setReviewIdx] = useState(0);
  useEffect(() => { setReviewIdx(0); }, [reviews.length]);

  const applyFinance = async () => {
    try {
      await publicApi.post("/enquiries", {
        kind: "finance",
        finance_required: true,
        finance_quote: { price, down_pct: downPct, term_months: term, monthly, rate_percent: rate },
        name: "Visitor", mobile: "+000", message: tr("x.finance_request_omr_m", { p0: price, p1: downPct, p2: term }),
      });
    } catch { /* swallow — form will be the real path */ }
    nav("/contact");
  };

  return (
    <>
      {/* ======================= HERO ======================= */}
      <section className="pub-hero-v2" data-testid="hero-section">
        {slides.map((src, i) => (
          <div
            key={i}
            className={`hero-slide ${i === slide ? "active" : ""}`}
            style={{ backgroundImage: `url("${src}")` }}
            aria-hidden={i !== slide}
          />
        ))}
        <div className="hero-scrim-x" />
        <div className="hero-scrim-b" />
        <div className="public-container hero-inner">
          <div className="hero-kicker">
            <span className="hero-rule" />
            <span>{t("px.certified")}</span>
          </div>
          <h1 className="hero-headline">
            <span className="thin">{lang === "ar" ? "متقنة." : tr("x.pre_owned")}</span>
            <span className="bold">{lang === "ar" ? "مُسبقة الملكية." : "perfected."}</span>
          </h1>
          <p className="hero-body" dir="auto">
            {settings?.meta_description || (lang === "ar"
              ? "سيارات معتمدة بسجلات نظيفة وأسعار شفافة في مسقط."
              : tr("x.hand_picked_hand_finished_cars_with_clea"))}
          </p>

          <form className="hero-search" onSubmit={submitSearch} data-testid="hero-searchbar">
            <div><label>{t("px.make")}</label>
              <select value={filter.make} onChange={(e) => setFilter({ ...filter, make: e.target.value })}>
                <option value="">{t("px.any")}</option>{(facets.makes || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><label>{t("px.body")}</label>
              <select value={filter.body_type} onChange={(e) => setFilter({ ...filter, body_type: e.target.value })}>
                <option value="">{t("px.any")}</option>{(facets.body_types || []).map((b) => <option key={b} value={b}>{lbl(b)}</option>)}
              </select>
            </div>
            <div><label>{t("px.year")}</label>
              <select value={filter.year} onChange={(e) => setFilter({ ...filter, year: e.target.value })}>
                <option value="">{t("px.any")}</option>{Array.from({ length: Math.max(0, (facets.year_max || 2026) - (facets.year_min || 2015) + 1) }, (_, i) => (facets.year_max || 2026) - i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div><label>{t("px.budget")}</label>
              <input type="number" placeholder="25,000" value={filter.budget} onChange={(e) => setFilter({ ...filter, budget: e.target.value })} />
            </div>
            <button type="submit" className="pub-cta primary big" data-testid="pub-search-btn">
              {t("px.search_n", { n: total || "" })}
            </button>
          </form>

          <div className="hero-tabs">
            {slides.map((_, i) => (
              <button key={i} className={`hero-tab ${i === slide ? "active" : ""}`} onClick={() => setSlide(i)}>
                <span className="num">{String(i + 1).padStart(2, "0")}</span>
                <span className="line"><span className="fill" style={{ animationDuration: i === slide ? "7s" : "0s" }} /></span>
                <span className="lbl">{[t("px.tab_1"), t("px.tab_2"), t("px.tab_3")][i] || ""}</span>
              </button>
            ))}
          </div>
          <div className="hero-scroll-cue">{t("px.scroll")}</div>
        </div>
      </section>

      {/* ======================= MAKES STRIP (dark) ======================= */}
      {makeCounts.length ? (
        <section className="pub-makes-strip" data-testid="makes-strip">
          <div className="public-container">
            <div className="makes-rail">
              {makeCounts.map((m) => (
                <button key={m.make} className="make-cell" onClick={() => nav(`/cars?make=${encodeURIComponent(m.make)}`)}>
                  <span className="mk">{m.make}</span>
                  <span className="n">{m.count}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ======================= INVENTORY (light) ======================= */}
      <section className="pub-inventory" data-testid="featured-section">
        <div className="public-container">
          <div className="inv-head">
            <div>
              <div className="kicker">{t("px.this_week")}</div>
              <h2 className="inv-title">{t("px.week_sel")}</h2>
            </div>
            <div className="pills">
              {BODY_FILTERS.map((b) => (
                <button key={b} className={`pill ${bodyTab === b ? "active" : ""}`} onClick={() => setBodyTab(b)}>{b === "All" ? t("px.all") : lbl(b)}</button>
              ))}
            </div>
          </div>
          <div className="inv-grid">
            {featuredFiltered.map((v) => (
              <a key={v.stock_number} href={`/cars/${v.stock_number}`} className="inv-card" data-testid={`fcard-${v.stock_number}`}>
                <div className="inv-img">
                  {v.photos?.[0]?.url ? <img src={v.photos[0].variants?.thumbnail || v.photos[0].url} width={400} height={300} alt={`${v.make} ${v.model}`} loading="lazy" decoding="async" /> : <div className="vcard-placeholder" data-testid={`fcard-placeholder-${v.stock_number}`}>{tr("x.no_photo")}</div>}
                  <div className="vcard-badges">
                    {v.status === "Reserved" ? <span className="vbadge reserved">{tr("x.reserved")}</span> : null}
                    {v.specification ? <span className="vbadge">{lbl(v.specification)}</span> : null}
                  </div>
                  <button className="heart" onClick={(e) => e.preventDefault()} aria-label={t("px.save")}><Heart size={16} strokeWidth={1.75} /></button>
                </div>
                <div className="inv-body">
                  <div className="inv-sub">{[v.model_year, lbl(v.specification || v.body_type)].filter(Boolean).join(" · ")}</div>
                  <div className="inv-name" style={{ textTransform: "capitalize" }}>{v.make} {v.model}</div>
                  <div className="inv-price">
                    <span dir="ltr">{v.public_price != null ? fmtMoney(v.public_price, settings) : tr("x.price_on_request")}</span>
                    <span className="inv-mo">{t("px.per_month", { n: Math.round(((v.public_price || v.price || 0) * 0.8 * (1 + 0.045 * 4)) / 48).toLocaleString("en-US") })}</span>
                  </div>
                  <div className="inv-specs">
                    <span>{(v.mileage || 0).toLocaleString()} {tr("x.km_unit")}</span>
                    <span>{lbl(v.fuel_type) || tr("x.petrol")}</span>
                    <span>{lbl(v.transmission) || tr("x.auto")}</span>
                    <span>{t("px.warranty")}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
          <div className="inv-foot">
            <button type="button" className="pub-cta dark big" onClick={() => nav("/cars")}>{t("px.view_all")} <span className="flip-rtl" style={{ display: "inline-block" }}>→</span></button>
          </div>
        </div>
      </section>

      {/* ======================= PROMISE (dark) ======================= */}
      {promiseShown.length ? (
        <section className="pub-promise" data-testid="promise-section">
          <div className="public-container promise-grid">
            <div className="promise-img" style={{ backgroundImage: `url(${slides[0]})` }} />
            <div className="promise-text">
              <div className="kicker on-dark">{t("px.promise_k")}</div>
              <h2 className="promise-h">{t("px.promise_h")}</h2>
              <div className="promise-rows">
                {promiseShown.slice(0, 3).map((p, i) => (
                  <div key={i} className="promise-row">
                    <span className="pn">{String(i + 1).padStart(2, "0")}</span>
                    <span className="pt">{p.title}</span>
                    <span className="ps">{p.subtitle || ""}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ======================= FINANCE CALCULATOR (light) ======================= */}
      {financeOn ? (
        <section className="pub-finance" data-testid="finance-section">
          <div className="public-container fin-grid">
            <div>
              <div className="kicker">{t("px.finance")}</div>
              <h2 className="fin-h">{t("px.fin_h1")}<br/><em>{t("px.fin_h2")}</em></h2>
              <div className="fin-row">
                <label>{t("px.car_price")}</label>
                <input type="range" min="3000" max="80000" step="500" value={price} onChange={(e) => setPrice(+e.target.value)} data-testid="fin-price" />
                <div className="fin-val">OMR {price.toLocaleString()}</div>
              </div>
              <div className="fin-row">
                <label>{t("px.down", { n: downPct })}</label>
                <input type="range" min="0" max="60" step="5" value={downPct} onChange={(e) => setDownPct(+e.target.value)} data-testid="fin-down" />
                <div className="fin-val">OMR {Math.round(price * downPct / 100).toLocaleString()}</div>
              </div>
              <div className="fin-row">
                <label>{t("px.term")}</label>
                <div className="fin-terms">
                  {terms.map((tm) => (
                    <button key={tm} type="button" className={term === tm ? "active" : ""} onClick={() => setTerm(tm)}>{t("px.months_short", { n: tm })}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="fin-result">
              <div className="kicker on-sec">{t("px.est_monthly")}</div>
              <div className="fin-monthly" dir="ltr">OMR <span>{monthly.toLocaleString("en-US")}</span></div>
              <div className="fin-meta">{t("px.fin_meta", { rate, term })}</div>
              <button type="button" className="pub-cta primary big" onClick={applyFinance} data-testid="fin-apply">{t("px.apply_fin")}</button>
              <div className="fin-disclaimer">{disclaimer}</div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ======================= SELL / TRADE-IN (secondary) ======================= */}
      <section className="pub-sell-v2" data-testid="sell-section">
        <div className="public-container sell-grid">
          <div>
            <h3 className="sell-h">{t("px.sell_h")}</h3>
            <p className="sell-p">{t("px.sell_p")}</p>
          </div>
          <form className="sell-fields" onSubmit={(e) => { e.preventDefault(); nav("/sell"); }} data-testid="sell-form-mini">
            <input placeholder={t("px.make_model")} />
            <input placeholder={t("px.year")} />
            <input placeholder={t("px.mileage")} />
            <input placeholder={t("px.mobile")} />
            <button type="submit" className="pub-cta primary big full">{t("px.value_btn")}</button>
          </form>
        </div>
      </section>

      {/* ======================= REVIEWS (light) ======================= */}
      {reviews.length ? (
        <section className="pub-reviews" data-testid="reviews-section">
          <div className="public-container">
            <div className="kicker mb-4" data-testid="reviews-title">{t("px.reviews")}</div>
            <div className="rev-head">
              {rating ? <div className="rev-rt" data-testid="reviews-rating">{Number(rating).toFixed(1)}</div> : null}
              {rating ? <div className="rev-stars">★ ★ ★ ★ ★</div> : null}
              <div className="rev-sub" data-testid="reviews-count">{t("px.reviews_n", { n: reviewCount || reviews.length })}</div>
            </div>
            <div className="rev-row">
              <div className="rev-switch">
                {reviews.slice(0, 3).map((_, i) => (
                  <button key={i} className={i === reviewIdx ? "active" : ""} onClick={() => setReviewIdx(i)}>{String(i + 1).padStart(2, "0")}</button>
                ))}
              </div>
              <div className="rev-body">
                <div className="rev-avatar" />
                <div>
                  <div className="rev-q">"{reviews[reviewIdx]?.quote}"</div>
                  <div className="rev-by">— {reviews[reviewIdx]?.by || ""}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ======================= VISIT US (dark) ======================= */}
      <section className="pub-visit-v2" data-testid="visit-section">
        <div className="public-container visit-grid">
          <div className="visit-text">
            <div className="kicker on-dark">{t("px.visit_k")}</div>
            <h2 className="visit-h">{t("px.visit_h")}</h2>
            <div className="visit-cells">
              <div className="vc"><div className="vl">{t("px.address")}</div><div className="vv">{settings?.address || "—"}</div></div>
              <div className="vc"><div className="vl">{t("px.phone")}</div><div className="vv"><span dir="ltr" className="ltr">{settings?.phone || "—"}</span></div></div>
              <div className="vc"><div className="vl">{t("px.hours")}</div><div className="vv">{settings?.opening_hours || "—"}</div></div>
              <div className="vc"><div className="vl">{t("px.email")}</div><div className="vv">{settings?.email || "—"}</div></div>
            </div>
            <div className="visit-actions">
              {settings?.whatsapp ? <a className="pub-cta wa" href={`https://wa.me/${settings.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noopener noreferrer">WhatsApp</a> : null}
              {(settings?.map_lat && settings?.map_lng) ? (
                <a className="pub-cta ghost-light" href={`https://maps.google.com/?q=${settings.map_lat},${settings.map_lng}`} target="_blank" rel="noopener noreferrer">{t("px.directions")}</a>
              ) : null}
            </div>
          </div>
          <div className="visit-img" style={{ backgroundImage: `url(${slides[1] || slides[0]})` }} />
        </div>
      </section>
    </>
  );
}
